'use client';

import { useState } from 'react';
import PostComposer from './PostComposer';
import FeedFilterBar from './FeedFilterBar';
import PostCard from './PostCard';

export type Post = {
  id: string;
  authorName: string;
  authorHandle: string;
  authorInitials: string;
  authorGradient: string;
  verified: boolean;
  timeAgo: string;
  content: string;
  topics: string[];
  intentScore: number;
  likes: number;
  comments: number;
  shares: number;
  bookmarks: number;
  liked: boolean;
  bookmarked: boolean;
  mediaType?: 'none' | 'code' | 'quote';
  mediaContent?: string;
};

const INITIAL_POSTS: Post[] = [
  {
    id: 'post-001',
    authorName: 'Riya Kapoor',
    authorHandle: '@riya.builds',
    authorInitials: 'RK',
    authorGradient: 'from-violet-500 to-cyan-500',
    verified: true,
    timeAgo: '3m',
    content: 'Just shipped a real-time intent classifier that adapts to what you\'re reading — not just what you\'ve liked in the past. The signal decay model is the key insight. The further back an interaction is, the less it should define your current feed.\n\nOpen-sourcing the decay function next week. 🧠',
    topics: ['machine-learning', 'intentgraph'],
    intentScore: 97,
    likes: 247,
    comments: 38,
    shares: 91,
    bookmarks: 52,
    liked: false,
    bookmarked: false,
    mediaType: 'none',
  },
  {
    id: 'post-002',
    authorName: 'Marcus Osei',
    authorHandle: '@marcus.design',
    authorInitials: 'MO',
    authorGradient: 'from-amber-500 to-orange-500',
    verified: true,
    timeAgo: '18m',
    content: 'Why most social feeds fail at discovery:\n\n→ They optimize for what you already know you like\n→ Not what you\'re about to become interested in\n→ The gap between "liked before" and "exploring now" is where all the interesting discovery happens\n\nNEXUS\'s IntentGraph is the first real attempt I\'ve seen to close that gap.',
    topics: ['ux-design', 'discovery'],
    intentScore: 91,
    likes: 183,
    comments: 29,
    shares: 64,
    bookmarks: 41,
    liked: true,
    bookmarked: false,
    mediaType: 'none',
  },
  {
    id: 'post-003',
    authorName: 'Priya Nair',
    authorHandle: '@priya.writes',
    authorInitials: 'PN',
    authorGradient: 'from-pink-500 to-rose-500',
    verified: true,
    timeAgo: '41m',
    content: 'The difference between a social network and an intelligent social network is whether it can tell the difference between who you were and who you\'re becoming.\n\nMost platforms answer the question "what did this person engage with?" NEXUS is trying to answer "what does this person need to find right now?" That\'s a fundamentally different product.',
    topics: ['tech-policy', 'social-networks'],
    intentScore: 89,
    likes: 412,
    comments: 67,
    shares: 148,
    bookmarks: 89,
    liked: false,
    bookmarked: true,
    mediaType: 'quote',
    mediaContent: '"The platform learns permitted signals about what a person is interested in now." — NEXUS Blueprint',
  },
  {
    id: 'post-004',
    authorName: 'James Whitfield',
    authorHandle: '@james.protocol',
    authorInitials: 'JW',
    authorGradient: 'from-emerald-500 to-teal-500',
    verified: false,
    timeAgo: '1h',
    content: 'Graph databases for social networks: when your social graph and intent graph share the same traversal primitives, discovery gets genuinely interesting.\n\nThe query "find me content that my follows\' follows are engaging with, filtered by my current intent vector" is 3 hops in a graph DB. In a relational DB it\'s 4 joins and a subquery.\n\nArchitecture matters for discovery.',
    topics: ['distributed-systems', 'graph-db'],
    intentScore: 88,
    likes: 156,
    comments: 22,
    shares: 43,
    bookmarks: 67,
    liked: false,
    bookmarked: false,
    mediaType: 'code',
    mediaContent: `MATCH (me:User {id: $userId})-[:FOLLOWS*1..2]->(creator:User)
<-[:AUTHORED]-(post:Post)-[:TAGGED]->(topic:Topic)
WHERE topic.id IN $intentTopics
  AND post.created_at > $cutoff
RETURN post ORDER BY post.score DESC LIMIT 50`,
  },
  {
    id: 'post-005',
    authorName: 'Aisha Mensah',
    authorHandle: '@aisha.product',
    authorInitials: 'AM',
    authorGradient: 'from-blue-500 to-indigo-500',
    verified: true,
    timeAgo: '2h',
    content: 'Hot take: the reason social media feels exhausting is not the content — it\'s the mismatch between what the algorithm thinks you want and what you actually need right now.\n\nWhen your feed knows the difference between "I always liked cooking videos" and "I\'m specifically looking for quick weeknight recipes this week" — that\'s when it stops feeling like work.',
    topics: ['product-thinking', 'ux-design'],
    intentScore: 86,
    likes: 329,
    comments: 54,
    shares: 112,
    bookmarks: 78,
    liked: false,
    bookmarked: false,
    mediaType: 'none',
  },
  {
    id: 'post-006',
    authorName: 'Kwame Asante',
    authorHandle: '@kwame.infra',
    authorInitials: 'KA',
    authorGradient: 'from-cyan-500 to-blue-500',
    verified: false,
    timeAgo: '3h',
    content: 'Running NEXUS\'s recommendation scoring at scale notes:\n\n• Intent match component is the heaviest compute — embedding similarity is O(n) against user intent vector\n• Caching the intent vector per-user with a 5-minute TTL drops p99 latency from 340ms to 94ms\n• Redis sorted sets for the candidate pool are the right call — O(log n) insertion and range queries\n\nThe 94ms median isn\'t magic. It\'s cache design.',
    topics: ['infrastructure', 'performance'],
    intentScore: 84,
    likes: 98,
    comments: 17,
    shares: 31,
    bookmarks: 44,
    liked: false,
    bookmarked: false,
    mediaType: 'none',
  },
  {
    id: 'post-007',
    authorName: 'Fatima Al-Hassan',
    authorHandle: '@fatima.trust',
    authorInitials: 'FA',
    authorGradient: 'from-rose-500 to-pink-500',
    verified: true,
    timeAgo: '4h',
    content: 'Trust architecture in social platforms is not a feature — it\'s the foundation.\n\nNEXUS\'s Trust Engine is designed in from day one: reports, moderation queues, audit logs, appeals paths. The cost of retrofitting this after launch is 10× the cost of building it correctly upfront.\n\nThe platforms that got this wrong are still paying that debt.',
    topics: ['trust-safety', 'platform-design'],
    intentScore: 82,
    likes: 267,
    comments: 41,
    shares: 88,
    bookmarks: 61,
    liked: false,
    bookmarked: true,
    mediaType: 'none',
  },
  {
    id: 'post-008',
    authorName: 'Tariq Okonkwo',
    authorHandle: '@tariq.ventures',
    authorInitials: 'TO',
    authorGradient: 'from-yellow-500 to-amber-500',
    verified: false,
    timeAgo: '5h',
    content: 'The NEXUS business model is interesting: they\'re not selling attention to advertisers as the primary loop. They\'re selling useful discovery.\n\nWhen a business shows up in your feed because your current intent matches what they offer — not because they paid for placement — that\'s a fundamentally different value proposition.\n\nQualified leads > raw impressions.',
    topics: ['startups', 'business-models'],
    intentScore: 79,
    likes: 144,
    comments: 26,
    shares: 52,
    bookmarks: 33,
    liked: true,
    bookmarked: false,
    mediaType: 'none',
  },
  {
    id: 'post-009',
    authorName: 'Naledi Dlamini',
    authorHandle: '@naledi.dev',
    authorInitials: 'ND',
    authorGradient: 'from-purple-500 to-violet-500',
    verified: false,
    timeAgo: '7h',
    content: 'Security note for anyone building on Supabase at scale:\n\nRow Level Security is not optional — it\'s the boundary between your users\' data. Test both the allowed AND the denied cases. The Supabase security advisor will catch the obvious gaps, but edge cases in complex join policies need manual review.\n\nFounder control principle: the service-role key never touches the browser. Ever.',
    topics: ['security', 'supabase'],
    intentScore: 77,
    likes: 201,
    comments: 33,
    shares: 74,
    bookmarks: 55,
    liked: false,
    bookmarked: false,
    mediaType: 'none',
  },
  {
    id: 'post-010',
    authorName: 'Yemi Adeyemi',
    authorHandle: '@yemi.creator',
    authorInitials: 'YA',
    authorGradient: 'from-teal-500 to-emerald-500',
    verified: true,
    timeAgo: '9h',
    content: 'First month on NEXUS as a creator:\n\n• 3.2K followers from 0\n• 47 posts published\n• Average IntentMatch score: 84%\n• Best performing topic: #distributed-systems\n\nThe difference from other platforms: I can actually see which topics are driving discovery vs. which ones my existing followers engage with. That distinction is invaluable for content strategy.',
    topics: ['creator-economy', 'analytics'],
    intentScore: 76,
    likes: 388,
    comments: 72,
    shares: 134,
    bookmarks: 97,
    liked: false,
    bookmarked: false,
    mediaType: 'none',
  },
];

export default function FeedCenter() {
  const [posts, setPosts] = useState<Post[]>(INITIAL_POSTS);
  const [activeFilter, setActiveFilter] = useState('for-you');

  const handleLike = (postId: string) => {
    setPosts((prev) =>
      prev.map((p) =>
        p.id === postId
          ? { ...p, liked: !p.liked, likes: p.liked ? p.likes - 1 : p.likes + 1 }
          : p
      )
    );
  };

  const handleBookmark = (postId: string) => {
    setPosts((prev) =>
      prev.map((p) =>
        p.id === postId ? { ...p, bookmarked: !p.bookmarked } : p
      )
    );
  };

  const handleNewPost = (content: string) => {
    const newPost: Post = {
      id: `post-${Date.now()}`,
      authorName: 'Riya Kapoor',
      authorHandle: '@riya.builds',
      authorInitials: 'RK',
      authorGradient: 'from-violet-500 to-cyan-500',
      verified: true,
      timeAgo: 'just now',
      content,
      topics: ['general'],
      intentScore: 100,
      likes: 0,
      comments: 0,
      shares: 0,
      bookmarks: 0,
      liked: false,
      bookmarked: false,
      mediaType: 'none',
    };
    setPosts((prev) => [newPost, ...prev]);
  };

  return (
    <div className="space-y-4">
      {/* Composer */}
      <PostComposer onPost={handleNewPost} />

      {/* Filter bar */}
      <FeedFilterBar activeFilter={activeFilter} onFilterChange={setActiveFilter} />

      {/* Posts */}
      <div className="space-y-3">
        {posts.map((post) => (
          <PostCard
            key={post.id}
            post={post}
            onLike={handleLike}
            onBookmark={handleBookmark}
          />
        ))}
      </div>
    </div>
  );
}